import { useNavigate } from 'react-router-dom';
import ItemForm, { ItemFormState } from './ItemForm';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useMutation } from '@apollo/client/react';
import { ADD_ITEM_MUTATION } from '../../mutations/ItemMutations';
import { GET_ITEMS_QUERY } from '../../queries/ItemQueries';
import { useCallback, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import { UnitType, validate as validateIngredient } from '../../models/Item';
import PageContainer from '../layout/PageContainer';

const INITIAL_FORM_VALUES: Partial<ItemFormState['values']> = {
  name: '',
  unitType: UnitType.POUND,
  isIngredient: true
};

export default function ItemCreate() {
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [createItem] = useMutation(
    ADD_ITEM_MUTATION, {
    refetchQueries: [{ query: GET_ITEMS_QUERY }],
  });

  const [formState, setFormState] = useState<ItemFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<ItemFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<ItemFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof ItemFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<ItemFormState['values']>) => {
        const { issues } = validateIngredient(values);

        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
  }, [setFormValues]);

  const handleFormSubmit = useCallback(async (itemFormValues: Partial<ItemFormState['values']>) => {
    const { issues } = validateIngredient(itemFormValues);

    let errors = null;
    let hasErrors = false;

    if (issues && issues.length > 0) {
      errors = Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message]));
      hasErrors = true;
    }

    if (hasErrors) {
      setFormErrors(errors || {});
      return;
    }

    setFormErrors({});

    try {
      const { data: result } = await createItem({
        variables: {
          input: {
            id: 0,
            name: itemFormValues.name || '',
            unitType: itemFormValues.unitType || UnitType.POUND,
            isIngredient: !!itemFormValues.isIngredient,
            description: itemFormValues.description,
            orderNotes: itemFormValues.orderNotes,
            standardShippingQuantity: +(itemFormValues.standardShippingQuantity || 0),
            minOrderQuantity: +(itemFormValues.minOrderQuantity || 0),
            maxOrderQuantity: +(itemFormValues.maxOrderQuantity || 0)
          }
        }
      });

      if (result?.added) {
        notifications.show('Item created successfully.', {
          severity: 'success',
          autoHideDuration: 3000,
        });

        navigate('/dashboard/items');
      } else {
        notifications.show(
          'Failed to create item. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    } catch (createError) {
      notifications.show(
        `Failed to create item. Reason: ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [createItem, navigate, notifications, setFormErrors]);

  return (
    <PageContainer
      title="New Item"
      breadcrumbs={[{ title: 'Items', path: '/dashboard/items' }, { title: 'New' }]}
    >
      <ItemForm
        formState={formState}
        onFieldChange={handleFormFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel="Create"
      />
    </PageContainer>
  );
}
